import { FC, useEffect, useMemo, useState } from "react";
import { Button, Col, Loader, Row } from "taltech-styleguide";
import { useField } from "formik";
import { RoomsTable } from "./RoomsTable";
import { generateRow } from "./helpers";
import { RowsInformation } from "./RowsInformation";
import { layoutSchema } from "./building-layout.schema";
import { ProgressWizardStepForm } from "../../wizard/ProgressWizardStepForm";
import { useWizard } from "../../wizard/context";
import { mapDataToDropdownOptions } from "../../wizard/helpers";
import { FeedbackMessage } from "@components-common/forms";
import { application } from "../../../../../_runtime/bootstrap";
import { useLoading } from "../../../../../components-common/_shared/loading/use-loading.hook";

/**
 * BuildingLayoutForm Component
 *
 * Renders the form for building layout configuration within a wizard step.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.title - The title of the form.
 * @param {Object} props.element - The initial form element data.
 * @returns {JSX.Element} - The rendered building layout form.
 */
export const BuildingLayoutForm: FC<any> = ({ ...props }) => {
  const { state } = useWizard();
  const { title, element } = props;

  const spaceFuncOptions = mapDataToDropdownOptions(
    state.spaceFunctions,
    "id",
    "name"
  );
  if (
    element.layout.rowFirst.rooms.length === 0 &&
    spaceFuncOptions.length > 0
  ) {
    element.layout.spaceFunctions = spaceFuncOptions[0].value;
  }

  const body = (
    <ProgressWizardStepForm
      element={element.layout}
      schema={layoutSchema}
      title={title}
    >
      <h3>{title}</h3>
      <BuildingLayout
        spaceFuncOptions={spaceFuncOptions}
        initialState={element.layout}
      />
    </ProgressWizardStepForm>
  );
  return body;
};

/**
 * Function: getSpaceFunctions
 *
 * This function fetches space functions based on the selected space function preset and updates the state accordingly.
 * It also resets the room values if the shouldUpdate flag is true.
 *
 * @param {Function} setLoading - Function to set the loading state.
 * @param {Function} setRowSpaceFunctionsOptions - Function to set the options for space functions.
 * @param {string} selectedSpaceFunctionPreset - The selected space function preset ID.
 * @param {Object} helpersRowFirst - Formik helpers for the first row.
 * @param {Object} fieldRowFirst - Formik field for the first row.
 * @param {Object} helpersRowSecond - Formik helpers for the second row.
 * @param {Object} fieldRowSecond - Formik field for the second row.
 * @param {boolean} shouldUpdate - Flag indicating whether to update the room values.
 *
 * Returns:
 * @returns {void}
 */
const getSpaceFunctions = (
  setLoading: any,
  setRowSpaceFunctionsOptions: any,
  selectedSpaceFunctionPreset: string,
  helpersRowFirst: any,
  fieldRowFirst: any,
  helpersRowSecond: any,
  fieldRowSecond: any,
  shouldUpdate: boolean
) => {
  setLoading(true);
  application
    .getHttpClient()
    .get(
      `/buildingms/presets-spaces/spaces-with-data?presetId=${selectedSpaceFunctionPreset}`
    )
    .then((response: any) => {
      const flattenedItems = response.body!.items.map((item: any) => {
        return {
          ...item.spaceFunction,
          spaceFunctionId: item.spaceFunction.id,
        };
      });
      const rowSpaceFuncs = mapDataToDropdownOptions(
        flattenedItems,
        "spaceFunctionId",
        "name"
      );
      setRowSpaceFunctionsOptions(rowSpaceFuncs);
      if (shouldUpdate) {
        helpersRowFirst.setValue({
          ...fieldRowFirst.value,
          rooms: [],
        });
        helpersRowSecond.setValue({
          ...fieldRowSecond.value,
          rooms: [],
        });
      }
    });
};

/**
 * BuildingLayout Component
 *
 * Renders the building layout section, including rows information and rooms table.
 *
 * @param {Object} props - Component properties.
 * @param {Array} props.spaceFuncOptions - Options for space functions dropdown.
 * @returns {JSX.Element} - The rendered building layout.
 */
export const BuildingLayout: FC<any> = ({ ...props }) => {
  const { spaceFuncOptions } = props;
  const initialState = props.initialState;
  const [loading, setLoading] = useLoading(true);
  const [rowSpaceFunctionsOptions, setRowSpaceFunctionsOptions] =
    useState<any[] | undefined>(undefined);
  const [spaceFunctionPreset] = useField("spaceFunctions");
  const selectedSpaceFunctionPreset = useMemo(
    () => spaceFunctionPreset.value,
    [spaceFunctionPreset.value]
  );
  const [layout] = useField("");
  const [fieldRowFirst, metaRowFirst, helpersRowFirst] = useField(`rowFirst`);
  const [fieldRowSecond, metaRowSecond, helpersRowSecond] =
    useField(`rowSecond`);
  const areRoomsAvailable =
    layout.value.rowFirst.rooms.length > 0 &&
    layout.value.rowSecond.rooms.length > 0;

  useEffect(() => {
    if (selectedSpaceFunctionPreset !== undefined) {
      getSpaceFunctions(
        setLoading,
        setRowSpaceFunctionsOptions,
        selectedSpaceFunctionPreset,
        helpersRowFirst,
        fieldRowFirst,
        helpersRowSecond,
        fieldRowSecond,
        selectedSpaceFunctionPreset !== initialState.spaceFunctions
      );
    } else setLoading(false);
  }, [selectedSpaceFunctionPreset]);

  useEffect(() => {
    if (rowSpaceFunctionsOptions) {
      setLoading(false);
    }
  }, [rowSpaceFunctionsOptions]);

  return (
    <>
      {loading && <Loader overlay />}
      {!loading && (
        <>
          {rowSpaceFunctionsOptions!.length === 0 && (
            <>
              <h6 className="text-danger fw-bold mb-2">
                Add space functions to the selected preset before proceeding.
              </h6>
            </>
          )}
          <RowsInformation spaceFuncOptions={spaceFuncOptions} />
          <Row className="mb-3 align-items-center justify-content-between">
            <Col lg="6" style={{ alignSelf: "flex-start" }}>
              <GenerateRoomsButton
                spaceFuncOptions={rowSpaceFunctionsOptions}
              />
              {areRoomsAvailable && (
                <RoomsTable
                  id="layout"
                  spaceFuncOptions={rowSpaceFunctionsOptions}
                />
              )}
            </Col>
            <Col lg="6">
              <img src="/ExampleModel.png" className="w-100 pt-3 pt-lg-0" />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

/**
 * GenerateRoomsButton Component
 *
 * Renders a button to generate rooms based on the current form data.
 *
 * @param {Object} props - Component properties.
 * @param {Array} props.spaceFuncOptions - Options for space functions dropdown.
 * @returns {JSX.Element} - The rendered button with functionality to generate rooms.
 */
const GenerateRoomsButton = ({ spaceFuncOptions }: any) => {
  const [fieldRowFirst, metaRowFirst, helpersRowFirst] = useField(`rowFirst`);
  const [fieldRowSecond, metaRowSecond, helpersRowSecond] =
    useField(`rowSecond`);

  const onGenerateRooms = () => {
    if (spaceFuncOptions === undefined || spaceFuncOptions.length === 0) return;
    const rowFirstRooms = generateRow({
      rowId: "R ",
      rowNumber: 1,
      width: fieldRowFirst.value.width,
      numberOfRooms: fieldRowFirst.value.numberOfRooms,
      spaceFuncOptions,
    });
    const rowSecondRooms = generateRow({
      rowId: "R ",
      rowNumber: 2,
      width: fieldRowSecond.value.width,
      numberOfRooms: fieldRowSecond.value.numberOfRooms,
      spaceFuncOptions,
    });

    helpersRowFirst.setValue(rowFirstRooms);
    helpersRowSecond.setValue(rowSecondRooms);
  };

  return (
    <div>
      <Button onClick={() => onGenerateRooms()}>Generate rooms</Button>
      <FeedbackMessage fieldId="rowFirst.numberOfRooms"></FeedbackMessage>
      <FeedbackMessage fieldId="rowSecond.numberOfRooms"></FeedbackMessage>
    </div>
  );
};

/**
 * Throws an error indicating the function is not implemented.
 *
 * @throws {Error} - Indicates the function is not implemented.
 */
function getSchema(): any {
  throw new Error("Function not implemented.");
}
